import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { twMerge } from "tailwind-merge";
import type { ReactionEvent } from "@core/features/video";

const FLOAT_DURATION_MS = 3_000;
const ANNOUNCE_THROTTLE_MS = 3_000;

export type Props = {
  reactions: ReactionEvent[];
  /** Display name used for accessible announcements. */
  senderName?: string;
  className?: string;
};

/**
 * Renders two layers per active reaction on a participant tile:
 *   1. A floating emoji that rises from the bottom and fades out over 2s.
 *   2. A persistent "last reaction" badge pinned to the top-left corner,
 *      visible as long as at least one reaction remains in Zustand (4s TTL).
 *
 * Respects `prefers-reduced-motion`: the float layer is suppressed entirely
 * and the badge appears without animation. The Zustand-driven 4s badge
 * lifetime is unchanged either way.
 *
 * Reactions are announced once per 3-second window per attendee via an
 * `aria-live="polite"` region to avoid spamming screen readers during
 * bursty reactions.
 */
export function VideoMeetingTileReactionOverlay({
  reactions,
  senderName,
  className,
}: Props) {
  const prefersReducedMotion = useReducedMotion();

  // "Most recent" reaction — reducer-based tiebreak on timestamp, not array
  // index. Guards against any path that inserts out of order.
  const latest = useMemo<ReactionEvent | null>(() => {
    if (reactions.length === 0) return null;
    return reactions.reduce((a, b) => (b.timestamp > a.timestamp ? b : a));
  }, [reactions]);

  // Per-attendee announcement throttle — a ref holds the last announcement
  // timestamp so we don't announce every single reaction during bursts.
  const lastAnnouncedAtRef = useRef<number>(0);
  const [announcement, setAnnouncement] = useState<string>("");

  useEffect(() => {
    if (!latest) return;
    const now = Date.now();
    if (now - lastAnnouncedAtRef.current < ANNOUNCE_THROTTLE_MS) return;
    lastAnnouncedAtRef.current = now;
    const prefix = senderName ? `${senderName} reacted with` : "Reaction:";
    setAnnouncement(`${prefix} ${latest.emoji}`);
  }, [latest, senderName]);

  if (reactions.length === 0) return null;

  return (
    // z-20 sits above the camera-off avatar layer (z-10 on the tile's
    // fallback container); without this, reactions are invisible when
    // the sender has their camera off.
    <div
      className={twMerge(
        "pointer-events-none absolute inset-0 z-20",
        className
      )}
      data-testid="video-tile-reactions"
    >
      {!prefersReducedMotion && (
        <AnimatePresence>
          {reactions.map((reaction) => (
            <Float key={reaction.id} reaction={reaction} />
          ))}
        </AnimatePresence>
      )}

      {latest && (
        <div
          className="absolute top-1 left-1 px-2 py-1.5 rounded-md bg-brand-900/80 backdrop-blur text-2xl leading-none select-none"
          data-testid="video-tile-reactions-badge"
        >
          <span aria-hidden="true">{latest.emoji}</span>
        </div>
      )}

      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="video-tile-reactions-announcer"
      >
        {announcement}
      </div>
    </div>
  );
}

/**
 * A single floating emoji. Derives "how far into the animation am I?" from
 * `reaction.timestamp` — a stable value on the event itself — rather than
 * from local mount-time state. This keeps the animation continuous if the
 * component remounts (e.g. when the parent tile is re-keyed on a grid/
 * breakpoint change from a window resize), and skips the float entirely if
 * the reaction is already older than the animation duration by the time it
 * renders.
 *
 * A setTimeout scheduled from the effect forces a re-render at the exact
 * expiration moment so the motion.div unmounts cleanly instead of sitting
 * invisibly in the DOM until the badge-side TTL fires on the parent.
 */
function Float({ reaction }: { reaction: ReactionEvent }) {
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const elapsed = Math.max(0, Date.now() - reaction.timestamp);

  useEffect(() => {
    const remaining = FLOAT_DURATION_MS - Math.max(0, Date.now() - reaction.timestamp);
    if (remaining <= 0) return;
    const t = setTimeout(forceUpdate, remaining);
    return () => clearTimeout(t);
  }, [reaction.timestamp]);

  if (elapsed >= FLOAT_DURATION_MS) return null;

  const progress = elapsed / FLOAT_DURATION_MS;
  const remainingMs = FLOAT_DURATION_MS - elapsed;

  return (
    <motion.div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 text-6xl leading-none select-none"
      initial={{ y: -60 * progress, opacity: 1 - progress }}
      animate={{ y: -60, opacity: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: remainingMs / 1000, ease: "easeOut" }}
      aria-hidden="true"
      data-testid="video-tile-reactions-float"
    >
      {reaction.emoji}
    </motion.div>
  );
}

export default VideoMeetingTileReactionOverlay;
